import { getToken, onMessage } from 'firebase/messaging'

import { registerFcmToken } from '@/features/auth/api/auth.api'
import { getFirebaseMessaging } from '@/shared/config/firebase'
import { useNotificationStore } from '@/shared/stores/notification.store'

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY

export async function setupFcmNotifications() {
  console.log('========== FCM SETUP START ==========')

  console.log('VAPID KEY EXISTS:', Boolean(VAPID_KEY))
  console.log('SERVICE WORKER SUPPORTED:', 'serviceWorker' in navigator)

  if (!VAPID_KEY || !('serviceWorker' in navigator)) {
    console.log('❌ FCM STOPPED: VAPID key or service worker missing')
    return null
  }

  try {
    console.log('Requesting notification permission...')

    const permission = await Notification.requestPermission()

    console.log('Notification permission:', permission)

    if (permission !== 'granted') {
      console.log('❌ FCM STOPPED: Notification permission not granted')
      return null
    }

    console.log('Registering Firebase service worker...')

    const registration = await navigator.serviceWorker.register(
      '/firebase-messaging-sw.js',
    )

    console.log('✅ Service worker registered:', registration)

    console.log('Getting Firebase messaging...')

    const messaging = await getFirebaseMessaging()

    console.log('Firebase messaging:', messaging)

    if (!messaging) {
      console.log('❌ FCM STOPPED: Firebase messaging is null')
      return null
    }

    console.log('Getting FCM token...')

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    })

    console.log('FCM TOKEN:', token)

    if (!token) {
      console.log('❌ FCM STOPPED: Token is empty')
      return null
    }

    console.log('Calling /auth/fcm-token...')

    const response = await registerFcmToken(token)

    console.log('✅ FCM TOKEN API SUCCESS:', response)

    return token
  } catch (error) {
    console.error('❌ FCM SETUP ERROR:', error)
    return null
  }
}

export async function getCurrentFcmToken() {
  if (!VAPID_KEY || !('serviceWorker' in navigator)) {
    return null
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration(
      '/firebase-messaging-sw.js',
    )

    if (!registration) {
      return null
    }

    const messaging = await getFirebaseMessaging()

    if (!messaging) {
      return null
    }

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    })

    return token || null
  } catch (error) {
    console.error('Failed to get current FCM token:', error)
    return null
  }
}

let foregroundUnsubscribe = null

export async function setupForegroundNotifications() {
  if (foregroundUnsubscribe) {
    return foregroundUnsubscribe
  }

  try {
    const messaging = await getFirebaseMessaging()

    if (!messaging) {
      return () => {}
    }

    foregroundUnsubscribe = onMessage(messaging, (payload) => {
      console.log('Received foreground notification:', payload)

      const title =
        payload.notification?.title || payload.data?.title || 'Superior CRM'

      const body =
        payload.notification?.body ||
        payload.data?.body ||
        'You have a new notification.'

      const data = payload.data || {}

      // Filter by role if provided in the payload and we're in a specific module
      const role = data.recipientRole
      if (role) {
        const path = window.location.pathname
        if (
          (path.startsWith('/marketing') && role !== 'marketing') ||
          (path.startsWith('/sales') && role !== 'sales') ||
          (path.startsWith('/admin') && role !== 'admin') ||
          (path.startsWith('/purchase') && role !== 'purchase')
        ) {
          console.log(`Skipping notification for ${role} as we are on ${path}`)
          return
        }
      }

      useNotificationStore.getState().addNotification({
        id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        title,
        body,
        type: data.type || 'general',
        data,
        read: false,
        createdAt: new Date().toISOString(),
      })

      // Show system OS notification if browser has granted permission
      if (
        typeof Notification !== 'undefined' &&
        Notification.permission === 'granted'
      ) {
        try {
          new Notification(title, {
            body,
            icon: '/favicon.ico',
          })
        } catch {
          // Ignore desktop notification error in some browser environments
        }
      }
    })

    return foregroundUnsubscribe
  } catch (error) {
    console.error('Failed to setup foreground notifications:', error)

    return () => {}
  }
}
